import { memo, useMemo, useRef } from 'react';
import {
  CountrySelector,
  usePhoneInput,
  FlagImage,
  guessCountryByPartialPhoneNumber,
  defaultCountries,
  type UsePhoneInputConfig
} from 'react-international-phone';
import 'react-international-phone/style.css';

import { Button, Input, Space, type InputRef } from 'antd';

type UseInternationalPhoneInputConfig = UsePhoneInputConfig;

export interface InternationalPhoneInputProps {
  placeholder?: string;
  countrySelectorId?: string;
  inputId?: string;
  inputName?: string;
  wrapperClassName?: string;
  onChange?: (value: string) => void;
  value?: string;
  defaultConfig?: Omit<UseInternationalPhoneInputConfig, 'onChange' | 'value'>;
}

const InternationalPhoneInput = memo(
  ({
    placeholder,
    countrySelectorId,
    inputId,
    inputName,
    wrapperClassName,
    defaultConfig,
    value,
    onChange
  }: InternationalPhoneInputProps) => {
    const inputRef = useRef<InputRef>(null);

    const phoneInput = usePhoneInput({
      value,
      onChange: data => {
        if (!onChange) {
          return;
        }

        onChange(data.phone);
      },
      ...defaultConfig
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = e.target.value;
      const isDeleting = nextValue.length < phoneInput.inputValue.length;
      const isEmpty = nextValue.trim() === '' || nextValue.trim() === '+';

      if (isDeleting || isEmpty) {
        phoneInput.handlePhoneValueChange(e);
        return;
      }

      const { country } = guessCountryByPartialPhoneNumber({
        phone: nextValue,
        countries: defaultConfig?.countries ?? defaultCountries
      });

      if (!country) {
        return;
      }

      phoneInput.handlePhoneValueChange(e);
    };

    const preferredCountries = useMemo(
      () => [
        'br',
        'us',
        'ca',
        'cn',
        'jp',
        'de',
        'uk',
        'fr',
        'it',
        'es',
        'mx',
        'ar',
        'cl',
        'co',
        'pe',
        'uy',
        'py',
        'bo',
        've',
        'ec',
        'pt',
        'nl',
        'be',
        'ch',
        'se',
        'no',
        'dk',
        'ie',
        'pl',
        'cz',
        'ru',
        'in',
        'kr',
        'au',
        'nz',
        'id',
        'th',
        'my',
        'sg',
        'vn',
        'ph',
        'hk',
        'tw',
        'il',
        'tr',
        'ae',
        'sa',
        'qa',
        'kw',
        'eg',
        'za',
        'ng',
        'ke',
        'gh',
        'dz',
        'ma',
        'tn',
        'mz',
        'ao',
        'cv',
        'gw',
        'st',
        'tl',
        'fi',
        'gr',
        'hu',
        'cr',
        'pa',
        'ni',
        'hn',
        'sv',
        'gt',
        'do',
        'cu',
        'tt',
        'jm',
        'bb',
        'bs'
      ],
      []
    );

    return (
      <Space.Compact className={wrapperClassName || ''}>
        <CountrySelector
          selectedCountry={phoneInput.country.iso2}
          preferredCountries={preferredCountries}
          onSelect={country => phoneInput.setCountry(country.iso2)}
          renderButtonWrapper={({ children, rootProps }) => (
            <Button
              {...rootProps}
              id={countrySelectorId}
              style={{
                padding: '0.25rem',
                paddingRight: '0.375rem',
                paddingLeft: '0.75rem',
                height: '100%',
                zIndex: 1
              }}
            >
              {children}
            </Button>
          )}
          dropdownStyleProps={{
            style: {
              top: '2.5625rem !important',
              position: 'absolute !important' as React.CSSProperties['position']
            }
          }}
        />

        <Input
          placeholder={placeholder || 'Número de telefone'}
          type='tel'
          value={phoneInput.inputValue}
          onChange={handleInputChange}
          ref={inputRef}
          className='react-international-phone-input'
          id={inputId}
          name={inputName || 'phone'}
          autoComplete='tel'
        />
      </Space.Compact>
    );
  }
);

const InternationalPhoneFlagImage = FlagImage;
const useInternationalPhoneInput = usePhoneInput;

export { useInternationalPhoneInput, InternationalPhoneFlagImage, InternationalPhoneInput };
export type { UseInternationalPhoneInputConfig };
